'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Calendar, CloudSun, Droplets, Eye, MapPin, Share2, Sunrise as SunriseIcon, Sunset as SunsetIcon, Clock, Wind } from 'lucide-react';
import { shareToFacebook, shareToTwitter, shareToWhatsApp } from '@/lib/share';
import { formatQualityScore, getQualityBadgeClasses, normalizeQualityCategory } from '@/lib/qualityScore';

interface Opportunity {
  id?: string;
  location: { name: string };
  date: string;
  type: 'sunrise' | 'sunset';
  quality: { category: string; score: number };
  time: string;
  goldenHour?: {
    start: string;
    end: string;
  };
  weather?: {
    cloudCoverTotal: number;
    humidity: number;
    visibility: number;
    wind: number;
  };
}

interface OpportunityCardProps {
  opp: Opportunity;
  index: number;
  formatDate: (dateStr: string) => string;
}

export function OpportunityCard({ opp, index, formatDate }: OpportunityCardProps) {
  const shareData = {
    location: opp.location.name,
    date: formatDate(opp.date),
    type: opp.type,
    quality: opp.quality.category,
    score: opp.quality.score,
    time: opp.time,
  };

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-2 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge variant="outline" className="text-xs">
                #{index + 1}
              </Badge>
              <Badge className={`${getQualityBadgeClasses(opp.quality.category)} border-0`}>
                {normalizeQualityCategory(opp.quality.category)} - {formatQualityScore(opp.quality.score)}
              </Badge>
            </div>
            <h3 className="text-xl font-bold flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <span className="truncate">{opp.location.name}</span>
            </h3>
            <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
              <Calendar className="w-4 h-4" />
              {formatDate(opp.date)}
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" aria-label="Share forecast">
                <Share2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => shareToTwitter(shareData)}>
                Share on Twitter
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => shareToFacebook(shareData)}>
                Share on Facebook
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => shareToWhatsApp(shareData)}>
                Share on WhatsApp
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Time Info */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 p-4 rounded-lg mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium capitalize flex items-center gap-2">
              {opp.type === "sunrise" ? <SunriseIcon className="w-4 h-4" /> : <SunsetIcon className="w-4 h-4" />}
              {opp.type}
            </span>
            <Clock className="w-4 h-4 text-amber-600" />
          </div>
          <div className="text-2xl font-bold text-amber-600">
            {opp.time}
          </div>
          {opp.goldenHour && (
            <div className="text-xs text-muted-foreground mt-1">
              Golden Hour: {opp.goldenHour.start} - {opp.goldenHour.end}
            </div>
          )}
        </div>

        {/* Weather Details */}
        {opp.weather && (
          <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
            <div className="flex items-center gap-1 sm:gap-2">
              <CloudSun className="w-3 h-3 sm:w-4 sm:h-4 text-slate-500 flex-shrink-0" />
              <span className="text-muted-foreground">
                {Math.round(opp.weather.cloudCoverTotal)}% clouds
              </span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Droplets className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0" />
              <span className="text-muted-foreground">
                {Math.round(opp.weather.humidity)}% humidity
              </span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4 text-slate-500 flex-shrink-0" />
              <span className="text-muted-foreground">
                {Math.round(opp.weather.visibility / 1000)} km vis
              </span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Wind className="w-3 h-3 sm:w-4 sm:h-4 text-slate-500 flex-shrink-0" />
              <span className="text-muted-foreground">
                {Math.round(opp.weather.wind)} mph
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
